import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { 
  Calendar, 
  MapPin, 
  Clock, 
  User, 
  DollarSign, 
  ChevronLeft, 
  ChevronRight, 
  AlertCircle, 
  RefreshCw,
  Play,
  Info,
  Heart,
  Gavel,
  ArrowLeft,
  Phone,
  FileText,
  Timer,
  X,
  ZoomIn
} from 'lucide-react';
import { fetchLotesLeilao, getLoteImages, getYouTubeEmbedUrl, formatDateOfBirth, formatSex, getLoteStatus, darLance } from '../services/leilaoApi';
import { fetchLeiloes } from '../services/api';
import { useToast } from '../hooks/use-toast';

const LeilaoDetalhes = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [lotes, setLotes] = useState([]);
  const [leilaoInfo, setLeilaoInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Estados para lance
  const [lanceModal, setLanceModal] = useState(false);
  const [loteAtual, setLoteAtual] = useState(null);
  const [valorLance, setValorLance] = useState(0);
  const [loadingLance, setLoadingLance] = useState(false);
  
  // Estados para detalhes
  const [detalhesModal, setDetalhesModal] = useState(false);
  const [loteDetalhes, setLoteDetalhes] = useState(null);

  // Estados para regulamento
  const [regulamentoModal, setRegulamentoModal] = useState(false);

  // Estados para contador
  const [timeRemaining, setTimeRemaining] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    total: 0
  });

  // Estados para modal de imagem
  const [imageModal, setImageModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Estados para modal de vídeo
  const [videoModal, setVideoModal] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');

  useEffect(() => {
    loadLeilaoData();
  }, [id]);

  const loadLeilaoData = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Carregando leilão ID:', id);
      
      // Buscar informações do leilão
      const leiloes = await fetchLeiloes();
      const leilao = leiloes.find(l => l.ID.toString() === id);
      
      if (!leilao) {
        setError('Leilão não encontrado');
        return;
      }
      
      console.log('Leilão encontrado:', leilao);
      setLeilaoInfo(leilao);
      
      // Buscar lotes do leilão
      const lotesData = await fetchLotesLeilao(id);
      console.log('Lotes carregados:', lotesData);
      
      // Verificar se há lotes disponíveis
      if (!lotesData || lotesData.length === 0) {
        // Não definir como erro, mas mostrar que não há lotes
        setLotes([]);
        return;
      }
      
      // Ordenar por número do lote
      const lotesSorted = lotesData.sort((a, b) => {
        const numA = parseInt(a.LOTEXX) || 0;
        const numB = parseInt(b.LOTEXX) || 0;
        return numA - numB;
      });
      
      setLotes(lotesSorted);
      
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError(err.message || 'Erro ao carregar dados do leilão');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      // A API envia no formato MM/DD/YYYY (formato americano)
      const [month, day, year] = dateString.split('/');
      return new Date(year, month - 1, day).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  // Função para calcular tempo restante
  const calculateTimeRemaining = (endDateTime) => {
    if (!endDateTime) return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 };
    
    try {
      // Parse da data HORA_FECHAMENTO_PRE (formato: "DD/MM/YYYY HH:mm")
      const [datePart, timePart] = endDateTime.split(' ');
      const [day, month, year] = datePart.split('/');
      const [hour, minute] = timePart.split(':');
      
      const endDate = new Date(year, month - 1, day, hour, minute);
      const now = new Date();
      const total = endDate.getTime() - now.getTime();
      
      if (total <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 };
      }
      
      const days = Math.floor(total / (1000 * 60 * 60 * 24));
      const hours = Math.floor((total % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((total % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((total % (1000 * 60)) / 1000);
      
      return { days, hours, minutes, seconds, total };
    } catch (error) {
      console.error('Erro ao calcular tempo restante:', error);
      return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 };
    }
  };

  // Effect para atualizar o contador a cada segundo
  useEffect(() => {
    if (!leilaoInfo?.HORA_FECHAMENTO_PRE) return;

    const updateCounter = () => {
      const remaining = calculateTimeRemaining(leilaoInfo.HORA_FECHAMENTO_PRE);
      setTimeRemaining(remaining);
    };

    // Atualiza imediatamente
    updateCounter();

    // Atualiza a cada segundo
    const interval = setInterval(updateCounter, 1000);

    return () => clearInterval(interval);
  }, [leilaoInfo?.HORA_FECHAMENTO_PRE]);

  // Função para verificar se o leilão está encerrado
  const isLeilaoEncerrado = () => {
    if (!leilaoInfo?.HORA_FECHAMENTO_PRE) return false;
    
    try {
      const [datePart, timePart] = leilaoInfo.HORA_FECHAMENTO_PRE.split(' ');
      const [day, month, year] = datePart.split('/');
      const [hour, minute] = timePart.split(':');
      
      const endDate = new Date(year, month - 1, day, hour, minute);
      const now = new Date();
      
      return now >= endDate;
    } catch (error) {
      return false;
    }
  };

  // Função para verificar se o lote foi vendido
  const isLoteVendido = (lote) => {
    if (!lote.LANMAX || !lote.LANATU) return false;
    return parseFloat(lote.LANATU) >= parseFloat(lote.LANMAX);
  };

  // Função para abrir modal de imagens
  const handleOpenImageModal = (images, startIndex = 0) => {
    setSelectedImages(images);
    setCurrentImageIndex(startIndex);
    setImageModal(true);
  };

  // Função para navegar entre imagens no modal
  const handlePreviousImage = () => {
    setCurrentImageIndex((prev) => 
      prev > 0 ? prev - 1 : selectedImages.length - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => 
      prev < selectedImages.length - 1 ? prev + 1 : 0
    );
  };

  const handleAbrirLance = (lote) => {
    // Verificar se o leilão está encerrado
    if (isLeilaoEncerrado()) {
      toast({
        title: "Leilão encerrado",
        description: "O prazo para lances neste leilão já expirou.",
        variant: "destructive"
      });
      return;
    }

    // Verificar se o lote foi vendido
    if (isLoteVendido(lote)) {
      toast({
        title: "Lote vendido",
        description: "Este lote já atingiu o valor máximo e foi vendido.",
        variant: "destructive"
      });
      return;
    }

    if (!user?.isLoggedIn) {
      toast({
        title: "Login necessário",
        description: "Faça login para dar lances",
        variant: "destructive"
      });
      navigate('/login');
      return;
    }
    
    setLoteAtual(lote);
    setValorLance(0);
    setLanceModal(true);
  };

  const handleDetalhes = (lote) => {
    setLoteDetalhes(lote);
    setDetalhesModal(true);
  };

  const adicionarValor = (valor) => {
    setValorLance(prev => prev + valor);
  };

  const confirmarLance = async () => {
    if (valorLance <= 0) {
      toast({
        title: "Valor inválido",
        description: "Informe um valor maior que zero",
        variant: "destructive"
      });
      return;
    }
    
    const novoValor = loteAtual.LANATU + valorLance;
    
    if (loteAtual.LANMAX > 0 && novoValor > loteAtual.LANMAX) {
      toast({
        title: "Lance acima do permitido",
        description: "O lance excede o valor máximo permitido",
        variant: "destructive"
      });
      return;
    }
    
    setLoadingLance(true);
    
    try {
      console.log('Enviando lance:', {
        loteId: loteAtual.ID,
        valor: novoValor,
        clienteId: user.clienteId
      });
      
      await darLance(loteAtual.ID, novoValor, user.clienteId);
      
      toast({
        title: "Lance realizado!",
        description: `Lance de ${formatCurrency(novoValor)} confirmado`,
        duration: 3000
      });
      
      // Atualizar o lance atual do lote
      setLotes(prev => prev.map(lote => 
        lote.ID === loteAtual.ID 
          ? { ...lote, LANATU: novoValor }
          : lote
      ));
      
      setLanceModal(false);
      setValorLance(0);
      
    } catch (err) {
      console.error('Erro ao enviar lance:', err);
      toast({
        title: "Erro no lance",
        description: err.message,
        variant: "destructive"
      });
    } finally {
      setLoadingLance(false);
    }
  };

  const abrirVideo = (videoUrlInput) => {
    if (!videoUrlInput) {
      console.log('URL de vídeo não fornecida');
      return;
    }
    
    console.log('Abrindo vídeo:', videoUrlInput);
    const embedUrl = getYouTubeEmbedUrl(videoUrlInput);
    
    if (embedUrl) {
      setVideoUrl(embedUrl);
      setVideoModal(true);
    } else {
      // Fallback para abrir em nova aba
      const cleanUrl = videoUrlInput.trim().replace(/[\r\n]/g, '');
      window.open(cleanUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const renderBotaoAcao = (lote) => {
    const status = getLoteStatus(lote);
    const leilaoEncerrado = isLeilaoEncerrado();
    const loteVendido = isLoteVendido(lote);
    
    // Verificar se o lote foi vendido por lance máximo
    if (loteVendido) {
      return (
        <Button 
          className="w-full bg-red-600 hover:bg-red-700"
          disabled
        >
          <Gavel className="h-4 w-4 mr-2" />
          VENDIDO
        </Button>
      );
    }
    
    // Verificar se o leilão foi encerrado por tempo
    if (leilaoEncerrado) {
      return (
        <Button 
          className="w-full bg-gray-600 hover:bg-gray-700"
          disabled
        >
          <Gavel className="h-4 w-4 mr-2" />
          ENCERRADO
        </Button>
      );
    }
    
    // Status original do leilaoApi (pode ser VENDIDO por outros motivos)
    if (status === 'VENDIDO') {
      return (
        <Button 
          className="w-full bg-red-600 hover:bg-red-700"
          disabled
        >
          <Gavel className="h-4 w-4 mr-2" />
          VENDIDO
        </Button>
      );
    }
    
    // Botão normal para dar lance
    return (
      <Button 
        onClick={() => handleAbrirLance(lote)}
        className="w-full bg-[rgb(238,29,37)] hover:bg-[rgb(238,29,37)]/90"
      >
        <Gavel className="h-4 w-4 mr-2" />
        DAR LANCE
      </Button>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gray-50 text-gray-900 py-8 border-b-4 border-[rgb(25,42,86)]">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between">
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                className="text-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)] hover:text-white mb-4 font-bold"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
            </div>
            
            <div className="text-center">
              <h1 className="text-3xl md:text-4xl font-black mb-2 text-[rgb(25,42,86)]">
                Carregando Leilão...
              </h1>
            </div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 py-16 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[rgb(25,42,86)] mx-auto mb-4"></div>
            <p className="text-gray-600">Carregando leilão...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && lotes.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header do Leilão */}
        <div className="bg-[rgb(46,48,146)] text-white py-8">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between">
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                className="text-white hover:bg-white/10 mb-4"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
            </div>
            
            <div className="text-center">
              <h1 className="text-3xl md:text-4xl font-bold mb-2">
                {leilaoInfo?.LEILAO || "Leilão"}
              </h1>
              {leilaoInfo && (
                <div className="flex flex-wrap justify-center gap-4 text-sm">
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    {formatDate(leilaoInfo?.DATLEI)}
                  </div>
                  <div className="flex items-center">
                    <MapPin className="h-4 w-4 mr-1" />
                    {leilaoInfo?.CIDADE}, {leilaoInfo?.ESTADO}
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    {leilaoInfo?.HORA_INICIO}h
                  </div>
                  <div className="flex items-center">
                    <User className="h-4 w-4 mr-1" />
                    {leilaoInfo?.LEILOE}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-16">
          <Alert className="border-[rgb(238,29,37)] bg-red-50">
            <AlertCircle className="h-4 w-4 text-[rgb(238,29,37)]" />
            <AlertDescription className="text-[rgb(238,29,37)]">
              {error}
            </AlertDescription>
          </Alert>
          
          <div className="text-center mt-8">
            <Button 
              onClick={loadLeilaoData}
              className="bg-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)]/90 flex items-center space-x-2 font-bold"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Tentar Novamente</span>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header do Leilão */}
      <div className="bg-gray-50 text-gray-900 py-8 border-b-4 border-[rgb(25,42,86)]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)] hover:text-white mb-4 font-bold"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
          </div>
          
          <div className="text-center">
            <h1 className="text-3xl md:text-4xl font-black mb-2 text-[rgb(25,42,86)]">
              {leilaoInfo?.LEILAO}
            </h1>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                {formatDate(leilaoInfo?.DATLEI)}
              </div>
              <div className="flex items-center">
                <MapPin className="h-4 w-4 mr-1" />
                {leilaoInfo?.CIDADE}, {leilaoInfo?.ESTADO}
              </div>
              <div className="flex items-center">
                <Clock className="h-4 w-4 mr-1" />
                {leilaoInfo?.HORA_INICIO}h
              </div>
              <div className="flex items-center">
                <User className="h-4 w-4 mr-1" />
                {leilaoInfo?.LEILOE}
              </div>
            </div>
            
            {/* Contador e Regulamento */}
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
              {/* Contador de Tempo */}
              {leilaoInfo?.HORA_FECHAMENTO_PRE && timeRemaining.total > 0 && (
                <div className="bg-white/10 backdrop-blur-sm rounded-lg px-6 py-3">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <Timer className="h-4 w-4" />
                    <span className="text-sm font-medium">Tempo Restante</span>
                  </div>
                  <div className="flex items-center gap-2 text-lg font-bold">
                    {timeRemaining.days > 0 && (
                      <div className="text-center">
                        <div>{timeRemaining.days}</div>
                        <div className="text-xs opacity-75">dias</div>
                      </div>
                    )}
                    <div className="text-center">
                      <div>{String(timeRemaining.hours).padStart(2, '0')}</div>
                      <div className="text-xs opacity-75">h</div>
                    </div>
                    <div>:</div>
                    <div className="text-center">
                      <div>{String(timeRemaining.minutes).padStart(2, '0')}</div>
                      <div className="text-xs opacity-75">min</div>
                    </div>
                    <div>:</div>
                    <div className="text-center">
                      <div>{String(timeRemaining.seconds).padStart(2, '0')}</div>
                      <div className="text-xs opacity-75">seg</div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Botão do Regulamento */}
              {leilaoInfo?.REGULAMENTO && (
                <Button
                  onClick={() => setRegulamentoModal(true)}
                  variant="outline"
                  className="bg-white/10 backdrop-blur-sm border-white/20 text-white hover:bg-white/20 hover:text-white"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Regulamento
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Lotes */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {lotes.length === 0 ? (
          <div className="text-center py-16">
            <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Nenhum lote encontrado</h3>
            <p className="text-gray-600">Este leilão ainda não possui lotes cadastrados.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {lotes.map((lote) => {
              const images = getLoteImages(lote);
              const status = getLoteStatus(lote);
              
              return (
                <Card key={lote.ID} className="border-2 hover:border-[rgb(25,42,86)] transition-all duration-200">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <Badge variant="secondary" className="text-lg font-bold">
                        Lote {lote.LOTEXX}
                      </Badge>
                      <Badge 
                        className={`${
                          status === 'VENDIDO' 
                            ? 'bg-red-600 hover:bg-red-700' 
                            : 'bg-green-600 hover:bg-green-700'
                        }`}
                      >
                        {status}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg leading-tight line-clamp-2">
                      {lote.DESLOT}
                    </CardTitle>
                  </CardHeader>

                  <div className="px-4">
                    <div className="relative mb-4">
                      <img
                        src={images[0]}
                        alt={lote.DESLOT}
                        className="w-full h-56 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity border-2 border-gray-200 hover:border-[rgb(25,42,86)]"
                        onClick={() => handleOpenImageModal(images, 0)}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/400x300/e5e7eb/9ca3af?text=Sem+Imagem';
                        }}
                      />
                      {images.length > 1 && (
                        <Badge 
                          className="absolute top-2 right-2 bg-black/70 cursor-pointer hover:bg-black/90"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenImageModal(images, 0);
                          }}
                        >
                          +{images.length - 1}
                        </Badge>
                      )}
                      {/* Indicador de clique para zoom */}
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs opacity-0 hover:opacity-100 transition-opacity pointer-events-none">
                        Clique para ampliar
                      </div>
                    </div>
                  </div>

                  <CardContent className="space-y-4">
                    {/* Informações do Animal */}
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Sexo:</span>
                        <span className="font-medium">{formatSex(lote.CATEGO)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Pelagem:</span>
                        <span className="font-medium text-right">{lote.PELAGE}</span>
                      </div>
                      {lote.DATNAS && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Nascimento:</span>
                          <span className="font-medium">{formatDateOfBirth(lote.DATNAS)}</span>
                        </div>
                      )}
                    </div>

                    <Separator />

                    {/* Valores */}
                    <div className="space-y-2">
                      <div className="text-center">
                        <p className="text-sm text-gray-600">Lance Atual</p>
                        <p className="text-xl font-black text-[rgb(25,42,86)]">
                          {formatCurrency(lote.LANATU)}
                        </p>
                      </div>
                      
                      {lote.LANMAX > 0 && (
                        <div className="text-center">
                          <p className="text-sm text-gray-600">Lance Alvo</p>
                          <p className="text-lg font-semibold text-gray-700">
                            {formatCurrency(lote.LANMAX)}
                          </p>
                        </div>
                      )}
                    </div>

                    <Separator />

                    {/* Botões de Ação */}
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        {lote.URLVIDEO && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => abrirVideo(lote.URLVIDEO)}
                            className="flex-1"
                          >
                            <Play className="h-4 w-4 mr-1" />
                            Vídeo
                          </Button>
                        )}
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDetalhes(lote)}
                          className="flex-1"
                        >
                          <Info className="h-4 w-4 mr-1" />
                          Detalhes
                        </Button>
                      </div>

                      {renderBotaoAcao(lote)}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal de Lance */}
      <Dialog open={lanceModal} onOpenChange={setLanceModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Dar Lance - Lote {loteAtual?.LOTEXX}</DialogTitle>
          </DialogHeader>
          
          {loteAtual && (
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="font-medium line-clamp-2">{loteAtual.DESLOT}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Lance atual: {formatCurrency(loteAtual.LANATU)}
                </p>
              </div>

              <div className="grid grid-cols-4 gap-2">
                {[10, 20, 50, 100].map((valor) => (
                  <Button
                    key={valor}
                    variant="outline"
                    onClick={() => adicionarValor(valor)}
                    className="text-sm"
                  >
                    +R${valor}
                  </Button>
                ))}
              </div>

              <div>
                <Label htmlFor="valorLance">Valor adicional</Label>
                <Input
                  id="valorLance"
                  type="number"
                  value={valorLance}
                  onChange={(e) => setValorLance(Number(e.target.value))}
                  placeholder="0"
                  min="0"
                />
              </div>

              <div className="text-center p-3 bg-gray-50 rounded">
                <p className="text-sm text-gray-600">Total do Lance</p>
                <p className="text-xl font-black text-[rgb(25,42,86)]">
                  {formatCurrency(loteAtual.LANATU + valorLance)}
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setLanceModal(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={confirmarLance}
                  disabled={loadingLance || valorLance <= 0}
                  className="flex-1 bg-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)]/90 font-bold"
                >
                  {loadingLance ? 'Processando...' : 'Confirmar Lance'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Detalhes */}
      <Dialog open={detalhesModal} onOpenChange={setDetalhesModal}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes - Lote {loteDetalhes?.LOTEXX}</DialogTitle>
          </DialogHeader>
          
          {loteDetalhes && (
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="text-xl font-bold">{loteDetalhes.DESLOT}</h3>
                {loteDetalhes.FILIACAO && (
                  <p className="text-gray-600 mt-1">{loteDetalhes.FILIACAO}</p>
                )}
              </div>

              {/* Galeria de Imagens */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {getLoteImages(loteDetalhes).map((imageUrl, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={imageUrl}
                      alt={`${loteDetalhes.DESLOT} - Imagem ${index + 1}`}
                      className="w-full h-64 object-cover rounded-lg border-2 border-gray-200 hover:border-[rgb(25,42,86)] transition-colors cursor-pointer"
                      onClick={() => handleOpenImageModal([imageUrl], 0)}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/400x300/e5e7eb/9ca3af?text=Imagem+Indisponível';
                      }}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity rounded-lg pointer-events-none"></div>
                  </div>
                ))}
              </div>

              {/* Informações Detalhadas */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <h4 className="font-semibold mb-2">Dados do Animal</h4>
                  <div className="space-y-1">
                    <div><span className="text-gray-600">Sexo:</span> {formatSex(loteDetalhes.CATEGO)}</div>
                    <div><span className="text-gray-600">Pelagem:</span> {loteDetalhes.PELAGE}</div>
                    {loteDetalhes.DATNAS && (
                      <div><span className="text-gray-600">Nascimento:</span> {formatDateOfBirth(loteDetalhes.DATNAS)}</div>
                    )}
                    {/* Campos condicionais baseados na espécie - mesmo campo, labels diferentes */}
                    {loteDetalhes.ESPECIES === 'EQUINOS' ? (
                      <>
                        {loteDetalhes.RPXXX && (
                          <div><span className="text-gray-600">RP:</span> {loteDetalhes.RPXXX}</div>
                        )}
                        {loteDetalhes.SBBXXX && (
                          <div><span className="text-gray-600">SBB:</span> {loteDetalhes.SBBXXX}</div>
                        )}
                      </>
                    ) : (loteDetalhes.ESPECIES === 'BOVINOS' || loteDetalhes.ESPECIES === 'OVINOS') ? (
                      <>
                        {loteDetalhes.RPXXX && (
                          <div><span className="text-gray-600">Tatuagem:</span> {loteDetalhes.RPXXX}</div>
                        )}
                        {loteDetalhes.SBBXXX && (
                          <div><span className="text-gray-600">HBB:</span> {loteDetalhes.SBBXXX}</div>
                        )}
                      </>
                    ) : (
                      <>
                        {/* Fallback: mostrar com labels genéricos */}
                        {loteDetalhes.RPXXX && (
                          <div><span className="text-gray-600">RP:</span> {loteDetalhes.RPXXX}</div>
                        )}
                        {loteDetalhes.SBBXXX && (
                          <div><span className="text-gray-600">SBB:</span> {loteDetalhes.SBBXXX}</div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Vendedor</h4>
                  <div className="space-y-1">
                    <div><span className="text-gray-600">Nome:</span> {loteDetalhes.NOMEXX}</div>
                    <div><span className="text-gray-600">Cidade:</span> {loteDetalhes.NOMECIDADE}, {loteDetalhes.NOMEESTADO}</div>
                    <div><span className="text-gray-600">Espécie:</span> {loteDetalhes.ESPECIES}</div>
                  </div>
                </div>
              </div>

              {/* Comentários */}
              {loteDetalhes.COMENTARIO && (
                <div>
                  <h4 className="font-semibold mb-2">Comentários</h4>
                  <p className="text-sm text-gray-700 text-justify whitespace-pre-line">
                    {loteDetalhes.COMENTARIO}
                  </p>
                </div>
              )}

              {/* Observações do Lote */}
              {loteDetalhes.OBSLOT && (
                <div>
                  <h4 className="font-semibold mb-2">Observações</h4>
                  <p className="text-sm text-gray-700 text-justify whitespace-pre-line">
                    {loteDetalhes.OBSLOT}
                  </p>
                </div>
              )}

              {/* Informações de Lance */}
              <div className="bg-gray-50 p-4 rounded">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="text-sm text-gray-600">Lance Atual</p>
                    <p className="text-lg font-black text-[rgb(25,42,86)]">
                      {formatCurrency(loteDetalhes.LANATU)}
                    </p>
                  </div>
                  {loteDetalhes.LANMAX > 0 && (
                    <div>
                      <p className="text-sm text-gray-600">Lance Alvo</p>
                      <p className="text-lg font-bold text-gray-700">
                        {formatCurrency(loteDetalhes.LANMAX)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal do Regulamento */}
      <Dialog open={regulamentoModal} onOpenChange={setRegulamentoModal}>
        <DialogContent className="sm:max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Regulamento do Leilão
            </DialogTitle>
          </DialogHeader>
          
          <div className="max-h-[60vh] overflow-y-auto">
            {leilaoInfo?.REGULAMENTO ? (
              <div className="prose max-w-none">
                <div 
                  className="text-sm leading-relaxed whitespace-pre-wrap break-words"
                  dangerouslySetInnerHTML={{ 
                    __html: leilaoInfo.REGULAMENTO.replace(/\n/g, '<br/>') 
                  }}
                />
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Regulamento não disponível no momento.</p>
              </div>
            )}
          </div>
          
          <div className="flex justify-end pt-4 border-t">
            <Button
              onClick={() => setRegulamentoModal(false)}
              className="bg-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)]/90 font-bold"
            >
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Vídeo */}
      <Dialog open={videoModal} onOpenChange={setVideoModal}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] p-0">
          <DialogHeader className="p-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              <Play className="h-5 w-5" />
              Vídeo do Lote
            </DialogTitle>
          </DialogHeader>
          
          <div className="p-4">
            {videoUrl && (
              <div className="relative aspect-video">
                <iframe
                  src={videoUrl}
                  title="Vídeo do Lote"
                  className="w-full h-full rounded-lg"
                  frameBorder="0"
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                />
              </div>
            )}
          </div>
          
          <div className="flex justify-end p-4 border-t">
            <Button
              onClick={() => {
                setVideoModal(false);
                setVideoUrl('');
              }}
              className="bg-[rgb(25,42,86)] hover:bg-[rgb(25,42,86)]/90 font-bold"
            >
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Visualização de Imagens */}
      <Dialog open={imageModal} onOpenChange={setImageModal}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] p-0">
          <div className="relative">
            {/* Header do Modal */}
            <div className="flex items-center justify-between p-4 border-b bg-gray-50">
              <div className="flex items-center gap-2">
                <ZoomIn className="h-5 w-5" />
                <span className="font-medium">
                  Imagem {currentImageIndex + 1} de {selectedImages.length}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setImageModal(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Área da Imagem */}
            <div className="relative bg-black">
              {selectedImages.length > 0 && (
                <img
                  src={selectedImages[currentImageIndex]}
                  alt={`Imagem ${currentImageIndex + 1}`}
                  className="w-full max-h-[70vh] object-contain"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/800x600/e5e7eb/9ca3af?text=Erro+ao+Carregar+Imagem';
                  }}
                />
              )}

              {/* Navegação entre imagens - apenas se há múltiplas imagens */}
              {selectedImages.length > 1 && (
                <>
                  {/* Botão Anterior */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white"
                    onClick={handlePreviousImage}
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </Button>

                  {/* Botão Próximo */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white"
                    onClick={handleNextImage}
                  >
                    <ChevronRight className="h-6 w-6" />
                  </Button>

                  {/* Indicadores de imagem */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {selectedImages.map((_, index) => (
                      <button
                        key={index}
                        className={`w-3 h-3 rounded-full transition-colors ${
                          index === currentImageIndex
                            ? 'bg-white'
                            : 'bg-white/50 hover:bg-white/70'
                        }`}
                        onClick={() => setCurrentImageIndex(index)}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Thumbnails das outras imagens */}
            {selectedImages.length > 1 && (
              <div className="p-4 bg-gray-50 border-t">
                <div className="flex gap-2 overflow-x-auto">
                  {selectedImages.map((imageUrl, index) => (
                    <button
                      key={index}
                      className={`flex-shrink-0 w-16 h-16 rounded border-2 overflow-hidden transition-all ${
                        index === currentImageIndex
                          ? 'border-[rgb(25,42,86)] ring-2 ring-[rgb(25,42,86)]/20'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      onClick={() => setCurrentImageIndex(index)}
                    >
                      <img
                        src={imageUrl}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/64x64/e5e7eb/9ca3af?text=?';
                        }}
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeilaoDetalhes;